import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { PackingBoxDto, ShipmentParcelDto } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { Badge, Button, Input, NumberInput, SearchSelect } from "../ui";
import type { PickListOrderDetail } from "./types";

const CUSTOM_BOX = "__custom__";

interface ParcelDraft {
  packingBoxId: string | null;
  useCustom: boolean;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  weightKg: string;
  label: string;
}

function parcelToDraft(parcel: ShipmentParcelDto): ParcelDraft {
  const useCustom = parcel.packingBoxId == null && (parcel.lengthCm != null || parcel.widthCm != null);
  return {
    packingBoxId: parcel.packingBoxId,
    useCustom: useCustom || parcel.packingBoxId == null,
    lengthCm: parcel.lengthCm != null ? String(parcel.lengthCm) : "",
    widthCm: parcel.widthCm != null ? String(parcel.widthCm) : "",
    heightCm: parcel.heightCm != null ? String(parcel.heightCm) : "",
    weightKg: parcel.weightGrams != null ? String(Math.round((parcel.weightGrams / 1000) * 1000) / 1000) : "",
    label: parcel.label ?? "",
  };
}

export function ParcelCard({
  parcel,
  order,
  boxes,
  onSaved,
}: {
  parcel: ShipmentParcelDto;
  order: PickListOrderDetail;
  boxes: PackingBoxDto[];
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(() => parcelToDraft(parcel));

  useEffect(() => {
    setDraft(parcelToDraft(parcel));
  }, [parcel]);

  const boxOptions = useMemo(
    () => [
      ...boxes.map((b) => ({
        value: b.id,
        label: `${b.name} (${b.lengthCm}×${b.widthCm}×${b.heightCm} cm)`,
      })),
      { value: CUSTOM_BOX, label: "Custom dimensions" },
    ],
    [boxes],
  );

  const selectedBoxValue = draft.useCustom ? CUSTOM_BOX : draft.packingBoxId ?? CUSTOM_BOX;

  const saveMutation = useMutation({
    mutationFn: () => {
      const lengthCm = Number.parseFloat(draft.lengthCm);
      const widthCm = Number.parseFloat(draft.widthCm);
      const heightCm = Number.parseFloat(draft.heightCm);
      const weightKg = Number.parseFloat(draft.weightKg);
      const body: Record<string, unknown> = {
        label: draft.label.trim() || null,
        weightGrams: Number.isFinite(weightKg) ? Math.round(weightKg * 1000) : null,
      };
      if (draft.useCustom) {
        body.packingBoxId = null;
        body.lengthCm = Number.isFinite(lengthCm) ? lengthCm : null;
        body.widthCm = Number.isFinite(widthCm) ? widthCm : null;
        body.heightCm = Number.isFinite(heightCm) ? heightCm : null;
      } else if (draft.packingBoxId) {
        body.packingBoxId = draft.packingBoxId;
      }
      return api(`/fulfilment/parcels/${parcel.id}`, { method: "PUT", body });
    },
    onSuccess: () => {
      toast("success", "Parcel saved");
      onSaved();
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/fulfilment/parcels/${parcel.id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Parcel removed");
      onSaved();
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Remove failed"),
  });

  function onBoxChange(value: string) {
    if (value === CUSTOM_BOX) {
      setDraft((d) => ({ ...d, useCustom: true, packingBoxId: null }));
      return;
    }
    const box = boxes.find((b) => b.id === value);
    const suggestedKg = (order.suggestedWeightGrams + (box?.tareGrams ?? 0)) / 1000;
    setDraft((d) => ({
      ...d,
      useCustom: false,
      packingBoxId: value,
      lengthCm: box ? String(box.lengthCm) : d.lengthCm,
      widthCm: box ? String(box.widthCm) : d.widthCm,
      heightCm: box ? String(box.heightCm) : d.heightCm,
      weightKg: d.weightKg || String(Math.round(suggestedKg * 1000) / 1000),
    }));
  }

  return (
    <div className="rounded-xl border border-line bg-white/[0.02] p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-white/70">
          {parcel.label || "Parcel"}
          {parcel.isComplete && <Badge color="green">Ready</Badge>}
        </span>
        {order.parcels.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-white/40 hover:text-red-400"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            Remove
          </Button>
        )}
      </div>
      <div className="sidebar-form">
        <Input
          label="Label"
          hint="Optional, e.g. Box 1 of 2"
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        />
        <SearchSelect
          label="Box preset"
          value={selectedBoxValue}
          onChange={onBoxChange}
          options={boxOptions}
          placeholder="Select box…"
        />
        {draft.useCustom && (
          <div className="form-row-3">
            <NumberInput
              label="L (cm)"
              value={draft.lengthCm}
              onValueChange={(value) => setDraft((d) => ({ ...d, lengthCm: value }))}
            />
            <NumberInput
              label="W (cm)"
              value={draft.widthCm}
              onValueChange={(value) => setDraft((d) => ({ ...d, widthCm: value }))}
            />
            <NumberInput
              label="H (cm)"
              value={draft.heightCm}
              onValueChange={(value) => setDraft((d) => ({ ...d, heightCm: value }))}
            />
          </div>
        )}
        <NumberInput
          label="Weight (kg)"
          hint={`Suggested ${Math.round((order.suggestedWeightGrams / 1000) * 1000) / 1000} kg contents`}
          value={draft.weightKg}
          onValueChange={(value) => setDraft((d) => ({ ...d, weightKg: value }))}
          blurFallback=""
        />
        <Button size="sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}
